import { SearchResultDataDc } from "@evergis/api";
import { CircularProgress } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../../../evergis/api";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { declension } from "../../../../utils/declension";
import { formatNumber } from "../../../../utils/formatNumber";
import { useLayersSearchContext } from "./context";
import { FeatureSearchResult } from "./FeatureSearchResult";
import { FeaturesSearchResultList, LoadMore } from "./styled";

export interface FeaturesSearchListProps {
  layer: LayerInfo;
  totalCount: number;
}

const SEARCH_LIMIT = 10;

export const FeaturesSearchList: FC<FeaturesSearchListProps> = ({ layer, totalCount }) => {
  const { t } = useTranslation("layerList");
  const { taskId } = useLayersSearchContext();
  const { name } = layer;
  const [currentOffset, setCurrentOffset] = useState<number>(0);
  const [currentItems, setCurrentItems] = useState<SearchResultDataDc[]>([]);
  const [loading, setLoading] = useState(false);

  const canLoadMore = currentOffset + SEARCH_LIMIT < totalCount;
  const moreResultsCount = totalCount - currentOffset - SEARCH_LIMIT;

  const loadMore = useCallback(async () => {
    const newOffset = currentOffset + (currentItems.length !== 0 ? SEARCH_LIMIT : 0);

    try {
      setLoading(true);

      const { items: newItems } = await api.universalSearch.searchResultDcGetSearchResult({
        offset: newOffset,
        limit: SEARCH_LIMIT,
        layerName: name,
        taskId,
      });

      setCurrentOffset(newOffset);
      setCurrentItems([...currentItems, ...(newItems as SearchResultDataDc[])]);
    } finally {
      setLoading(false);
    }
  }, [name, currentOffset, currentItems, taskId]);

  useEffect(() => {
    loadMore();
  }, []);

  return (
    <FeaturesSearchResultList>
      {currentItems.map((searchResult, index) => (
        <FeatureSearchResult key={index} searchResult={searchResult} layerName={name} />
      ))}
      {loading && (
        <div style={{ marginLeft: "auto", marginRight: "auto" }}>
          <CircularProgress />
        </div>
      )}
      {canLoadMore && !loading && (
        <LoadMore onClick={loadMore}>
          {t("more")} {formatNumber(moreResultsCount)}{" "}
          {declension(t("result1"), t("result3"), t("result3"), moreResultsCount)}
        </LoadMore>
      )}
    </FeaturesSearchResultList>
  );
};
